import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, calculateAge, debounce } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search,
  FileText,
  Download,
  Eye,
  Loader2,
  Users,
  FileDown,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function PatientSummariesPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(null);

  // Only Admin and Physicians can access this page
  const canAccess = user?.role === 'admin' || user?.role === 'physician';

  useEffect(() => {
    if (canAccess) {
      fetchPatients();
    }
  }, [canAccess]);

  const fetchPatients = async (search = '') => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const response = await patientsAPI.getAll(params);
      setPatients(response.data);
    } catch (err) {
      toast.error('Failed to fetch patients');
    }
    setLoading(false);
  };

  const debouncedSearch = debounce((query) => {
    fetchPatients(query);
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleGeneratePDF = async (patient) => {
    setGeneratingPDF(patient.id);
    try {
      // Call backend PDF generation endpoint
      const response = await api.get(`/api/patients/${patient.id}/summary/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${patient.first_name}_${patient.last_name}_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Summary PDF generated for ${patient.first_name} ${patient.last_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate PDF');
    }
    setGeneratingPDF(null);
  };

  const handleBulkExport = async () => {
    toast.info('Generating summaries for selected patients...');
    let successCount = 0;
    
    for (const patient of patients.slice(0, 5)) { // Limit to first 5 for bulk
      try {
        const response = await api.get(`/api/patients/${patient.id}/summary/pdf`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${patient.first_name}_${patient.last_name}_Summary.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        successCount++;
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (err) {
        console.error(`Failed to generate PDF for ${patient.id}`);
      }
    }
    toast.success(`Exported ${successCount} patient summaries!`);
  };

  if (!canAccess) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">
          Only Administrators and Physicians can access Patient Summaries.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="patient-summaries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Patient Summaries</h1>
          <p className="text-muted-foreground mt-1">
            Generate and export comprehensive patient summary PDFs
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="gap-1">
            <Shield className="h-3 w-3" />
            {user?.role === 'admin' ? 'Admin' : 'Physician'} Access
          </Badge>
        </div>
      </div>

      {/* Search and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search patients by name, ID, or email..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-9"
                data-testid="patient-search"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={handleBulkExport}
              className="gap-2"
              data-testid="bulk-export-btn"
            >
              <FileDown className="h-4 w-4" />
              Bulk Export (First 10)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">Patient Summary PDF Contents</h3>
              <p className="text-sm text-blue-800 mt-1">
                Each summary includes: Demographics, Active Problems, Medications, Allergies, 
                Insurance Info, Recent Encounters, and Clinical Notes. Perfect for referrals, 
                transfers, or patient records.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {loading ? 'Loading...' : `${patients.length} Patient${patients.length !== 1 ? 's' : ''}`}
          </CardTitle>
          <CardDescription>
            Click "Generate PDF" to create a comprehensive summary for any patient
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : patients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
              <h3 className="text-lg font-medium mb-1">No patients found</h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'Try adjusting your search' : 'No patients in the system'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead className="hidden md:table-cell">ID</TableHead>
                    <TableHead className="hidden sm:table-cell">Age</TableHead>
                    <TableHead className="hidden lg:table-cell">Contact</TableHead>
                    <TableHead className="hidden md:table-cell">Allergies</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow key={patient.id} data-testid={`patient-row-${patient.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="font-semibold text-sm text-primary">
                              {patient.first_name[0]}{patient.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">
                              {patient.first_name} {patient.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {patient.gender}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {patient.patient_id}
                        </code>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {calculateAge(patient.date_of_birth)} yrs
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="text-sm">
                          {patient.phone || patient.email || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {patient.allergies && patient.allergies.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {patient.allergies.slice(0, 2).map((allergy, idx) => (
                              <Badge 
                                key={idx} 
                                variant="destructive" 
                                className="text-xs bg-amber-100 text-amber-800"
                              >
                                {allergy}
                              </Badge>
                            ))}
                            {patient.allergies.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{patient.allergies.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/emr/patients/${patient.id}`)}
                            data-testid={`view-patient-${patient.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGeneratePDF(patient)}
                            disabled={generatingPDF === patient.id}
                            className="gap-1"
                            data-testid={`generate-pdf-${patient.id}`}
                          >
                            {generatingPDF === patient.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            <span className="hidden sm:inline">PDF</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
